/** PAN: AAAAA9999A — 5 letters, 4 digits, 1 letter */
export const PAN_PATTERN = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
export const PAN_MAX_LENGTH = 10;

/** GSTIN: 99AAAAA9999A9Z9 */
export const GST_PATTERN =
  /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
export const GST_MAX_LENGTH = 15;

/** Aadhaar: 12 digits, first digit 2–9 */
export const AADHAAR_PATTERN = /^[2-9]{1}[0-9]{11}$/;
export const AADHAAR_MAX_LENGTH = 12;

export type KycFieldKind = 'pan' | 'gst' | 'aadhaar' | null;

export function detectKycFieldKind(
  fieldName?: string,
  fieldType?: string,
  fieldLabel?: string,
): KycFieldKind {
  const type = (fieldType || '').replace(/[\s_-]+/g, '').toLowerCase();
  const name = (fieldName || '').replace(/[\s_-]+/g, '').toLowerCase();
  const label = (fieldLabel || '').replace(/[\s_-]+/g, '').toLowerCase();

  if (
    type.includes('aadhaar') ||
    type.includes('aadhar') ||
    name.includes('aadhaar') ||
    name.includes('aadhar') ||
    label.includes('aadhaar') ||
    label.includes('aadhar')
  ) {
    return 'aadhaar';
  }
  if (
    type === 'gstnumber' ||
    type === 'gst' ||
    name.includes('gst') ||
    label.includes('gst')
  ) {
    return 'gst';
  }
  if (
    type === 'pannumber' ||
    type === 'pan' ||
    type.includes('pan_number') ||
    name === 'pan' ||
    name === 'pannumber' ||
    label === 'pannumber'
  ) {
    return 'pan';
  }
  return null;
}

function isLetter(ch: string) {
  return /^[A-Z]$/.test(ch);
}

function isDigit(ch: string) {
  return /^[0-9]$/.test(ch);
}

export function sanitizePanInput(raw: string): string {
  const upper = raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
  let out = '';

  for (const ch of upper) {
    if (out.length >= PAN_MAX_LENGTH) break;
    const pos = out.length;
    if (pos < 5) {
      if (isLetter(ch)) out += ch;
    } else if (pos < 9) {
      if (isDigit(ch)) out += ch;
    } else if (isLetter(ch)) {
      out += ch;
    }
  }

  return out;
}

export function sanitizeGstInput(raw: string): string {
  const upper = raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
  let out = '';

  for (const ch of upper) {
    if (out.length >= GST_MAX_LENGTH) break;
    const pos = out.length;

    if (pos < 2) {
      if (isDigit(ch)) out += ch;
    } else if (pos < 7) {
      if (isLetter(ch)) out += ch;
    } else if (pos < 11) {
      if (isDigit(ch)) out += ch;
    } else if (pos === 11) {
      if (isLetter(ch)) out += ch;
    } else if (pos === 12) {
      if (/^[1-9A-Z]$/.test(ch)) out += ch;
    } else if (pos === 13) {
      if (ch === 'Z') out += ch;
    } else if (/^[0-9A-Z]$/.test(ch)) {
      out += ch;
    }
  }

  return out;
}

/** Digits only, max 12; first digit must be 2–9 when present. */
export function sanitizeAadhaarInput(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  let out = '';
  for (const ch of digits) {
    if (out.length >= AADHAAR_MAX_LENGTH) break;
    if (out.length === 0) {
      if (/^[2-9]$/.test(ch)) out += ch;
    } else if (isDigit(ch)) {
      out += ch;
    }
  }
  return out;
}

export function sanitizeKycInput(kind: KycFieldKind, raw: string): string {
  if (kind === 'pan') return sanitizePanInput(raw);
  if (kind === 'gst') return sanitizeGstInput(raw);
  if (kind === 'aadhaar') return sanitizeAadhaarInput(raw);
  return raw;
}
